import { ChangeDetectionStrategy, Component, ViewChild, Injector } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { PermissionFacade } from '../../../../../../../../modules/permission/facades/permission.facade';
import { PermSetLine } from '../../../../../../../../models/perm-set.model';
import { PermSetActions } from '../../../../../../../../modules/permission/perm-set-controller/actions';
import { PermSetSelectors } from '../../../../../../../../modules/permission/perm-set-controller/selectors';
import { PermissionStates } from '../../../../../../permission-controller/root';
import { ViewColumnComponent } from '../../../../../../../../view-column.component';
import { SubSink } from 'subsink2';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateReturn: boolean,
  selectedIndex: number
}

@Component({
  selector: 'app-perm-set-add-lines-edit',
  templateUrl: './perm-set-add-lines-edit.component.html',
  styleUrls: ['./perm-set-add-lines-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class PermSetAddLinesEditComponent extends ViewColumnComponent {

  @ViewChild(MatTabGroup, {static: true}) matTab: MatTabGroup;

  protected readonly index = 4;
  private localState: LocalState;

  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateReturn$ = this.componentStore.select(state => state.localState.deactivateReturn);
  readonly selectedIndex$ = this.componentStore.select(state => (state.localState.selectedIndex));

  prevIndex: number;
  private prevLocalState: any;

  public form: FormGroup;

  // keep track of form being enabled/disabled
  formDisabled: boolean = false;

  // no leakage of subscribe calls
  private subSink = new SubSink;

  selectedPermSetLine: PermSetLine;

  //translation variable
  title: string;
  back_button: string;
  save_button: string;
  permission_name_text: string;
  permission_code_text: string;
  permission_targets_text: string;
  default_target_text: string;
  select_target_text: string;
  please_select_a_target_text: string;
  description_text: string;

  constructor(
    private fb: FormBuilder,
    private toasterService: ToastrService,
    private readonly store: Store<PermissionStates>,
    private viewColFacade: PermissionFacade,
    private componentStore: ComponentStore<{localState: LocalState}>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.title = res.edit_permission_set_line;
        this.back_button = res.back_button;
        this.save_button = res.save_button;
        this.permission_name_text = res.permission_name;
        this.permission_code_text = res.permission_code;
        this.permission_targets_text = res.permission_targets;
        this.default_target_text = res.default_target;
        this.select_target_text = res.select_target;
        this.please_select_a_target_text = res.please_select_a_target;
        this.description_text = res.description;
      });
    } else {
      // Fallback for applets that don't implement translation
      this.title = 'Edit Permission Set Line';
      this.back_button = 'Back';
      this.save_button = 'SAVE';
      this.permission_name_text = 'Permission Name';
      this.permission_code_text = 'Permission Code';
      this.permission_targets_text = 'Permission Targets';
      this.default_target_text = 'Default Target';
      this.select_target_text = 'Select Target';
      this.please_select_a_target_text = 'Please select a target!';
      this.description_text = 'Description';
    }
  }

  /**
   * Lifecycle method: Called when component is first initialised.
   */
  ngOnInit(): void {
    // initial configurations for local state
    this.subSink.sink = this.localState$.subscribe( a => {
      this.localState = a;
      this.componentStore.setState( {localState: a} );
    });
    this.subSink.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subSink.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);

    this.form = this.fb.group({
      target: ['', Validators.required],
      description: [''],

      // uneditable
      code: [''],
      name: [''],
      all_targets: [''],
      target_hdr_name: [''],
      target_hdr_table: ['']
    });

    const permSetLine$ = this.store.select(PermSetSelectors.selectPermSetLine);

    this.subSink.sink = permSetLine$
    .subscribe(permSetLine => {
      this.selectedPermSetLine = permSetLine;

      this.form.patchValue({
        code: permSetLine.app_perm_set_line.code,
        name: permSetLine.app_perm_set_line.name,
        target_hdr_table: permSetLine.app_perm_set_line.target_hdr_table,
        target_hdr_name: permSetLine.default_target_view_name,
        all_targets: permSetLine.app_perm_set_line.target_options_json['target_hdr_table_view_name'],
        description: permSetLine.app_perm_set_line.description,
        target: permSetLine.app_perm_set_line.target_hdr_table == null ? "NO TARGET" : permSetLine.default_target_hdr_name
      })
    })
  }

  /**
   * This function is called whe the user clicks on the save button.
   * This will dispatch an NgRx action to update the permission set line.
   *
   */
   onSubmit() {

    // update the permission set line description
    this.selectedPermSetLine.app_perm_set_line.description = this.form.value.description;
    // update the perm set line in the store
    this.store.dispatch(PermSetActions.selectPermSetLine({permSetLine: this.selectedPermSetLine}));

    this.onReturn();
  }

  /**
   * Trigger the add target component.
   */
  selectTemplate() {
    if (this.selectedPermSetLine.app_perm_set_line.target_hdr_table != null) {
      this.viewColFacade.updateInstance(this.index,
        {
          ...this.localState,
          deactivateReturn: true
        });
      this.viewColFacade.onNext(5);
    } else {
      this.toasterService.error(
        'No targets to configure!',
        'Error',
        {
          tapToDismiss: true,
          progressBar: true,
          timeOut: 1300
        }
      );
    }
  }

  /**
   * Return to the previous drawer/view column.
   */
   onReturn() {
    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateAdd: false,
      deactivateList: false,
      deactivateReturn: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
  }

  /**
   * Lifecycle method: Called when this component is destroyed.
   */
   ngOnDestroy() {
    if (this.matTab) {
      this.viewColFacade.updateInstance(this.index, {
        ...this.localState,
        selectedIndex: this.matTab.selectedIndex
      });
    }
    this.subSink.unsubscribe();
  }

}
